import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, Eye, Edit, Trash2, Calendar, User, AlertCircle, CheckCircle2 } from 'lucide-react';
import AttachmentPanel from '../components/AttachmentPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Tasks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('estado') || '');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterClient, setFilterClient] = useState(searchParams.get('cliente') || '');
  const [filterResponsable, setFilterResponsable] = useState(searchParams.get('responsable') || '');
  const [showFinalized, setShowFinalized] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadTasks();
    loadClients();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterStatus, filterPriority, filterClient, filterResponsable, tasks, showFinalized]);

  const loadTasks = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tasks`);
      setTasks(data);
      setFilteredTasks(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients`);
      setClients(data);
    } catch (error) {
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/users`);
      setUsers(data);
    } catch (error) {
    }
  };

  const applyFilters = () => {
    let filtered = tasks;

    // Ocultar finalizadas por defecto, excepto si showFinalized está activo o se filtra explícitamente por estado
    if (!showFinalized && !filterStatus) {
      filtered = filtered.filter((t) => t.estado !== 'Finalizada');
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.titulo?.toLowerCase().includes(searchLower) ||
          t.descripcion?.toLowerCase().includes(searchLower) ||
          t.client_name?.toLowerCase().includes(searchLower)
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((t) => t.estado === filterStatus);
    }

    if (filterPriority) {
      filtered = filtered.filter((t) => t.prioridad === filterPriority);
    }

    if (filterClient) {
      filtered = filtered.filter((t) => t.client_id === filterClient);
    }

    if (filterResponsable) {
      filtered = filtered.filter((t) => t.responsable_id === filterResponsable);
    }

    setFilteredTasks(filtered);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('¿Está seguro de eliminar esta tarea?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${taskId}`);
      loadTasks();
    } catch (error) {
      alert('Error al eliminar la tarea');
    }
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const handleChangeStatus = async (taskId, newStatus) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/tasks/${taskId}/status`,
        { estado: newStatus },
        {  }
      );
      loadTasks();
    } catch (error) {
      alert('Error al actualizar el estado');
    }
  };

  const handleViewDetail = (task) => {
    setSelectedTask(task);
    setShowDetailModal(true);
  };

  const getStatusBadge = (estado) => {
    const badges = {
      Pendiente: 'bg-indigo-100 text-indigo-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      Finalizada: 'bg-emerald-100 text-emerald-800',
    };
    return badges[estado] || 'bg-slate-100 text-slate-800';
  };

  const getPriorityBadge = (prioridad) => {
    const badges = {
      Alta: 'bg-red-100 text-red-800',
      Media: 'bg-amber-100 text-amber-800',
      Baja: 'bg-slate-100 text-slate-800',
    };
    return badges[prioridad] || 'bg-slate-100 text-slate-800';
  };

  const getDaysRemaining = (fechaLimite) => {
    const today = new Date();
    const deadline = new Date(fechaLimite);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando tareas...</div>
      </div>
    );
  }

  const isRestrictedRole = user?.role === 'Auxiliar Contable' || user?.role === 'Asistente Contable';

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Tareas
          </h1>
          <p className="text-sm text-slate-700 leading-relaxed mt-2">Gestión de tareas y asignaciones</p>
        </div>
        {!isRestrictedRole && (
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
                data-testid="create-task-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Tarea
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Crear Nueva Tarea</DialogTitle>
              </DialogHeader>
              <TaskForm
                clients={clients}
                users={users}
                onSuccess={() => {
                  setShowCreateModal(false);
                  loadTasks();
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar tareas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="Pendiente">Pendiente</option>
              <option value="En proceso">En proceso</option>
              <option value="Finalizada">Finalizada</option>
            </select>
          </div>
          <div>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Todas las prioridades</option>
              <option value="Alta">Alta</option>
              <option value="Media">Media</option>
              <option value="Baja">Baja</option>
            </select>
          </div>
          <div>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Todos los clientes</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.razon_social || `${client.primer_nombre} ${client.primer_apellido}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filterResponsable}
              onChange={(e) => setFilterResponsable(e.target.value)}
              className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Todos los responsables</option>
              {users.map((usr) => (
                <option key={usr._id} value={usr._id}>
                  {usr.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {tasks.filter((t) => t.estado === 'Finalizada').length > 0 && !filterStatus && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <button
              onClick={() => setShowFinalized(!showFinalized)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                showFinalized
                  ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="toggle-finalized-tasks"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {showFinalized ? 'Ocultar finalizadas' : `Ver finalizadas (${tasks.filter((t) => t.estado === 'Finalizada').length})`}
            </button>
          </div>
        )}
      </div>

      {/* Tasks Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3">Título</th>
                <th className="px-6 py-3">Cliente</th>
                <th className="px-6 py-3">Responsable</th>
                <th className="px-6 py-3">Fecha Límite</th>
                <th className="px-6 py-3">Prioridad</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-700">
              {filteredTasks.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                    No se encontraron tareas
                  </td>
                </tr>
              ) : (
                filteredTasks.map((task) => {
                  const daysRemaining = getDaysRemaining(task.fecha_limite);
                  const isOverdue = daysRemaining < 0 && task.estado !== 'Finalizada';

                  return (
                    <tr key={task._id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{task.titulo}</p>
                          {task.descripcion && (
                            <p className="text-xs text-slate-500 mt-1 truncate max-w-xs">
                              {task.descripcion}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">{task.client_name}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {task.responsable_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>{task.fecha_limite}</span>
                          {isOverdue && (
                            <span className="text-xs text-red-600 font-medium">
                              (Vencida)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(
                            task.prioridad
                          )}`}
                        >
                          {task.prioridad}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                            task.estado
                          )}`}
                        >
                          {task.estado}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {isRestrictedRole ? (
                            task.estado === 'Finalizada' ? (
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge('Finalizada')}`}>Finalizada</span>
                            ) : (
                              <button
                                onClick={() => handleChangeStatus(task._id, 'Finalizada')}
                                className="px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 cursor-pointer transition-colors"
                                data-testid={`finalize-task-${task._id}`}
                              >
                                Marcar Finalizada
                              </button>
                            )
                          ) : (
                            <select
                              value={task.estado}
                              onChange={(e) => handleChangeStatus(task._id, e.target.value)}
                              className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-300 ${getStatusBadge(task.estado)}`}
                              data-testid={`status-select-${task._id}`}
                            >
                              <option value="Pendiente">Pendiente</option>
                              <option value="En proceso">En proceso</option>
                              <option value="Finalizada">Finalizada</option>
                            </select>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(task)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {!isRestrictedRole && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(task)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(task._id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar Tarea</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <TaskForm
              task={selectedTask}
              clients={clients}
              users={users}
              onSuccess={() => {
                setShowEditModal(false);
                setSelectedTask(null);
                loadTasks();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalles de la Tarea</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Título</p>
                  <p className="text-sm text-slate-900 mt-1 font-medium">{selectedTask.titulo}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Estado</p>
                  <span
                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusBadge(
                      selectedTask.estado
                    )}`}
                  >
                    {selectedTask.estado}
                  </span>
                </div>
                {selectedTask.descripcion && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Descripción</p>
                    <p className="text-sm text-slate-900 mt-1">{selectedTask.descripcion}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Cliente</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedTask.client_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsable</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedTask.responsable_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Prioridad</p>
                  <span
                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getPriorityBadge(
                      selectedTask.prioridad
                    )}`}
                  >
                    {selectedTask.prioridad}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Fecha Creación</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedTask.fecha_creacion}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Fecha Límite</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedTask.fecha_limite}</p>
                </div>
                {selectedTask.observaciones && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Observaciones</p>
                    <p className="text-sm text-slate-900 mt-1">{selectedTask.observaciones}</p>
                  </div>
                )}
              </div>
              <div className="border-t border-slate-200 pt-4 mt-2">
                <AttachmentPanel entityType="task" entityId={selectedTask._id} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Task Form Component
const TaskForm = ({ task, clients, users, onSuccess }) => {
  const [formData, setFormData] = useState({
    titulo: task?.titulo || '',
    descripcion: task?.descripcion || '',
    client_id: task?.client_id || '',
    responsable_id: task?.responsable_id || '',
    fecha_limite: task?.fecha_limite || '',
    prioridad: task?.prioridad || 'Media',
    estado: task?.estado || 'Pendiente',
    observaciones: task?.observaciones || '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (task) {
        await axios.put(`${BACKEND_URL}/api/tasks/${task._id}`, formData);
      } else {
        await axios.post(`${BACKEND_URL}/api/tasks`, formData);
      }
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar la tarea');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Título *</Label>
          <Input
            name="titulo"
            value={formData.titulo}
            onChange={handleChange}
            required
            placeholder="Título de la tarea"
          />
        </div>

        <div className="md:col-span-2">
          <Label>Descripción</Label>
          <textarea
            name="descripcion"
            value={formData.descripcion}
            onChange={handleChange}
            className="flex min-h-[80px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Descripción detallada de la tarea"
          />
        </div>

        <div>
          <Label>Cliente *</Label>
          <select
            name="client_id"
            value={formData.client_id}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="">Seleccionar cliente...</option>
            {clients.map((client) => (
              <option key={client._id} value={client._id}>
                {client.razon_social || `${client.primer_nombre} ${client.primer_apellido}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label>Responsable *</Label>
          <select
            name="responsable_id"
            value={formData.responsable_id}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="">Seleccionar responsable...</option>
            {users.map((usr) => (
              <option key={usr._id} value={usr._id}>
                {usr.name} - {usr.role}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label>Fecha Límite *</Label>
          <Input
            type="date"
            name="fecha_limite"
            value={formData.fecha_limite}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <Label>Prioridad *</Label>
          <select
            name="prioridad"
            value={formData.prioridad}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="Alta">Alta</option>
            <option value="Media">Media</option>
            <option value="Baja">Baja</option>
          </select>
        </div>

        <div>
          <Label>Estado *</Label>
          <select
            name="estado"
            value={formData.estado}
            onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            required
          >
            <option value="Pendiente">Pendiente</option>
            <option value="En proceso">En proceso</option>
            <option value="Finalizada">Finalizada</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <Label>Observaciones</Label>
          <textarea
            name="observaciones"
            value={formData.observaciones}
            onChange={handleChange}
            className="flex min-h-[60px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Notas adicionales u observaciones"
          />
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={loading} className="bg-[#1F3864] hover:bg-[#172b4d] text-white">
          {loading ? (task ? 'Actualizando...' : 'Creando...') : task ? 'Actualizar Tarea' : 'Crear Tarea'}
        </Button>
      </div>
    </form>
  );
};

export default Tasks;
